import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../i18n";
import { Marke } from "./index";
import { STUFENRANG } from "../daten/faelle";
import type { Fall, Stufe } from "../daten/faelle";
import { VERTEILBAR, fallDatum, fallTag, fallWort, fallspaltenLeersatz } from "../daten/fallsatz";
import { LAUF_TEXT } from "../daten/meldungstext";

/* ══════════════════════════════════════════════════════════════════
   Die drei Spalten der Startseite (24.09.2026, Paket 3, 51-meldungen.md).

   Bis hierher standen vier Karten nebeneinander, und jede trennte nach
   einem anderen Merkmal — „Zu prüfen" nach Art, „Was hakt" nach
   Dringlichkeit, „Meine Aufgaben" nach Besitz, „Was ist zu tun?" nach
   Herkunft im Code. Dieselbe Sache stand an bis zu fünf Stellen.

   Jetzt eine Liste (`GET /faelle`) und drei Spalten, je nach Rolle:

   | Disposition, Verwaltung | Meine Aufgaben · Zu verteilen · Zu entscheiden |
   | alle anderen           | Meine Aufgaben · Frei zum Übernehmen · Zu entscheiden |

   **Eine Zeile je Auftrag oder Gerät** (Entscheidung M4): Hat ein Auftrag
   drei offene Punkte, sind es drei Punkte in einer Zeile. **Sortiert nach
   Stufe** — blockiert, überfällig, heute; „Demnächst" steht eingeklappt
   darunter und ersetzt die Fristen. „Was hakt" gibt es nicht mehr als
   Karte: Was dort stand, ist jetzt eine rote Zeile in der passenden Spalte.
   ══════════════════════════════════════════════════════════════════ */

type Zeile = { schluessel: string; stufe: Stufe; faelle: Fall[] };

const STUFENMARKE: Record<Stufe, "" | "acht" | "stopp"> = {
  demnaechst: "", heute: "acht", ueberfaellig: "acht", blockiert: "stopp",
};

/** Je Auftrag, sonst je Gerät oder Mensch, sonst je Fall eine Zeile. */
function buendeln(faelle: Fall[]): Zeile[] {
  const map = new Map<string, Fall[]>();
  for (const f of faelle) {
    const k = f.vorgang ? `v:${f.vorgang}` : f.kennung ? `k:${f.kennung}` : f.schluessel;
    map.set(k, [...(map.get(k) ?? []), f]);
  }
  return [...map.entries()]
    .map(([schluessel, liste]) => ({
      schluessel,
      faelle: [...liste].sort((a, b) => STUFENRANG[b.stufe] - STUFENRANG[a.stufe]),
      stufe: liste.reduce<Stufe>((s, f) => (STUFENRANG[f.stufe] > STUFENRANG[s] ? f.stufe : s), "demnaechst"),
    }))
    .sort((a, b) => STUFENRANG[b.stufe] - STUFENRANG[a.stufe]
      || (a.faelle[0]?.faellig ?? a.faelle[0]?.seit ?? "").localeCompare(b.faelle[0]?.faellig ?? b.faelle[0]?.seit ?? ""));
}

/** Aufteilen nach Spalte — die Regel aus dem Kopf dieser Datei. */
export function spaltenAus(faelle: Fall[], ich: string, gruppen: string[], lenkt: boolean) {
  const meineGruppen = new Set(gruppen);
  const entscheiden = faelle.filter((f) => f.form === "entscheidung");
  const hinweise = faelle.filter((f) => f.form === "hinweis");
  const aufgaben = faelle.filter((f) => f.form === "aufgabe");
  if (lenkt) {
    const hof = aufgaben.filter((f) => f.gruppe === "technik" && f.art !== "verteilen");
    const schon = new Set(hof.map((f) => `${f.belegung_id}:${f.art}`));
    const verteilen = [
      ...hof,
      ...aufgaben.filter((f) => f.art === "verteilen" && !schon.has(`${f.belegung_id}:${f.daten["aufgabe"] ?? ""}`)),
    ];
    const meine = aufgaben.filter((f) => f.gruppe !== "technik" && f.art !== "verteilen"
      && (f.besitzer === null || f.besitzer.id === ich));
    return { meine, zweite: verteilen, entscheiden, hinweise };
  }
  const meine = aufgaben.filter((f) => f.besitzer?.id === ich
    || (f.besitzer === null && !meineGruppen.has(f.gruppe))
    || (f.stufe === "blockiert" && f.eskalation !== null && meineGruppen.has(f.eskalation)));
  const frei = aufgaben.filter((f) => f.besitzer === null && meineGruppen.has(f.gruppe));
  return { meine, zweite: frei, entscheiden, hinweise };
}

function Fallzeile({ z, mitBesitzer }: { z: Zeile; mitBesitzer: boolean }) {
  const t = useT();
  const gehZu = useNavigate();
  const erster = z.faelle[0]!;
  /* **Jeder Punkt nennt sein Gerät** (25.09.2026). Hängen mehrere Punkte
     an einem Auftrag, steht im Titel der Auftrag und nicht das Gerät des
     ersten Punkts — sonst las sich „AB-08" als Überschrift auch über dem
     Punkt von VS-01. */
  const mehrere = z.faelle.length > 1;
  /* **Ein Lauf heißt, wie er unter Hintergrundläufe heißt** (15.10.2026):
     Bis hierher stand im Titel `lex_kunden` — der Schlüssel, nicht der Name. */
  const laufname = erster.art === "lauf_fehler" && erster.kennung && LAUF_TEXT[erster.kennung]
    ? t(LAUF_TEXT[erster.kennung]!) : null;
  const titel = (mehrere && erster.vorgang
    ? [erster.kunde, erster.vorgang]
    : [laufname ?? erster.kennung, erster.kunde, erster.vorgang && erster.vorgang !== erster.kennung ? erster.vorgang : null])
    .filter(Boolean).join(" · ");
  /* **Was noch niemand hat, wird eingeteilt** (Luca, 25.09.2026; seit dem
     08.10.2026 auf eigener Seite, docs/65): In „Zu verteilen" führt eine
     Aufgabe ohne Menschen nicht in den Auftrag, sondern nach „Einteilen", an
     ihren Tag — dort liegt sie unter „Offen" und wird gleich zugeteilt. */
  const unverteilt = mitBesitzer
    ? z.faelle.find((f) => f.besitzer === null && VERTEILBAR.has(f.art) && fallTag(f))
    : undefined;
  const weg = unverteilt ? `/einteilen?tag=${fallTag(unverteilt)!}` : z.faelle.find((f) => f.weg)?.weg ?? null;
  /* **Jeder Punkt führt in sein Blatt** (D-22/D-61, Durchgang 03.10.2026).
     „Ohne Unterschrift annehmen" und „Ohne Pflichtfoto annehmen" standen in
     einer Zeile „PB-Tiefbau Süd · V-2026-0021", und ein Klick öffnete nur den
     Auftrag — das Protokoll bzw. die Pflichtfotos suchte man dort von Hand.
     Die API hängt jetzt `?protokoll=` / `?fotos=` an (`dienste/faelle.ts`).
     Zeigen die Punkte einer Zeile auf verschiedene Wege, ist jeder Punkt
     sein eigener Knopf und die Zeile selbst keiner. */
  const wege = new Set(z.faelle.map((f) => f.weg).filter((w): w is string => !!w));
  const jePunkt = !unverteilt && mehrere && wege.size > 1;
  const punkte = (
    <span className="fallpunkte">
      {/* **Gerät · was Tag · Stufe** (25.09.2026). Luca las „Übergeben ·
          überfällig" als die Übergabe am 29.09. — überfällig war die vom
          24.09., an einem anderen Gerät. Jeder Punkt trägt deshalb sein
          Gerät, seinen Tag und, wenn es mehrere sind, seine eigene Stufe
          in seiner eigenen Farbe. */}
      {z.faelle.map((f) => {
        // Im Titel steht das Gerät nur, solange die Zeile einen Punkt hat.
        const kennung = mehrere && erster.vorgang && f.kennung && f.kennung !== f.vorgang ? f.kennung : null;
        const datum = fallDatum(f, t);
        const inhalt = (
          <>
            {kennung ? <span className="fallpunkt-kennung">{kennung}</span> : null}
            {kennung ? " · " : ""}
            {fallWort(f, t)}
            {datum ? ` ${datum}` : ""}
            {f.daten["grund"] ? ` · „${f.daten["grund"]}“` : ""}
            {mehrere ? ` · ${t(`fall.stufe.${f.stufe}`)}` : ""}
            {mitBesitzer ? ` · ${f.besitzer ? f.besitzer.name : t("fall.frei")}` : ""}
          </>
        );
        return jePunkt && f.weg ? (
          <button
            key={f.schluessel}
            type="button"
            className={`fallpunkt-wort mit-weg ${f.stufe}`}
            onClick={() => { if (f.weg) void gehZu({ href: f.weg }); }}
          >
            {inhalt}
          </button>
        ) : (
          <span key={f.schluessel} className={`fallpunkt-wort ${f.stufe}`}>{inhalt}</span>
        );
      })}
    </span>
  );
  const innen = (
    <>
      <span className={`fallpunkt ${z.stufe}`} aria-hidden="true" />
      <span className="fallzeile-leib">
        <b>{titel || t(`fall.art.${erster.art}`)}</b>
        {punkte}
      </span>
      <span className="fallzeile-stufe">
        <Marke art={STUFENMARKE[z.stufe]}>{t(`fall.stufe.${z.stufe}`)}</Marke>
      </span>
    </>
  );
  if (jePunkt) return <div className={`fallzeile je-punkt ${z.stufe}`}>{innen}</div>;
  return (
    <button
      type="button"
      className={`fallzeile ${z.stufe}`}
      // `href`, nicht `to`: Der Weg kann `?protokoll=` oder `?fotos=` tragen.
      onClick={() => { if (weg) void gehZu({ href: weg }); }}
      disabled={!weg}
    >
      {innen}
    </button>
  );
}

export function Fallspalte({ titel, faelle, leer, mitBesitzer = false, weg, status, abfrage }: {
  titel: string; faelle: Fall[]; leer: string; mitBesitzer?: boolean;
  /** Ein Verweis rechts im Kartenkopf („Im Brett verteilen ›"), statt einer eigenen Fußleiste. */
  weg?: { wort: string; beiKlick: () => void } | undefined;
  /** Eine leise Statuszeile unter der Liste (Lexware-Zustand) — ohne eigenen Balken. */
  status?: ReactNode;
  /** Der Stand von `GET /faelle` — ohne Antwort kein Leerzustand (N10-03). */
  abfrage?: { isPending: boolean; isError: boolean; hatDaten: boolean };
}) {
  const t = useT();
  const [spaeterOffen, setSpaeterOffen] = useState(false);
  const jetzt = buendeln(faelle.filter((f) => f.stufe !== "demnaechst"));
  const spaeter = buendeln(faelle.filter((f) => f.stufe === "demnaechst"));
  const blockiert = jetzt.filter((z) => z.stufe === "blockiert").length;
  return (
    <div className="karte start-karte fallspalte">
      <div className="karte-kopf">
        <h4>{t(titel)}</h4>
        {jetzt.length ? (
          <Marke art={blockiert ? "stopp" : "acht"} punkt>
            {blockiert ? t("fall.zahlBlockiert", { zahl: jetzt.length, blockiert }) : jetzt.length}
          </Marke>
        ) : null}
        {weg ? (
          <button type="button" className="fall-kopf-weg" onClick={weg.beiKlick}>
            {t(weg.wort)} ›
          </button>
        ) : null}
      </div>
      {/* ══ Signal (07.10.2026): kein Fuß mehr ══
          Luca: *„Demnächst, im Brett verteilen … das sieht blöd aus."* Drei
          verschiedene Leisten am Kartenfuß, jede in eigener Farbe, und eine
          Karte, die leer 340 px hoch stand. Nach dem Muster (dashboard.md):
          Kopf, Liste, leiser Fuß. Der Weg ins Brett steht rechts im Kopf, das
          Spätere als leiser Satz am Fuß, der sich in die Liste aufklappt, und
          der Lexware-Zustand als Satz auf derselben Linie. */}
      <div className="start-karte-leib">
        {jetzt.length === 0
          ? <p className="fall-leer">{t(abfrage ? fallspaltenLeersatz(abfrage, leer) : leer)}</p>
          : null}
        {jetzt.map((z) => <Fallzeile key={z.schluessel} z={z} mitBesitzer={mitBesitzer} />)}
        {spaeter.length && spaeterOffen ? (
          <div className="fall-spaeter-gruppe">
            {spaeter.map((z) => <Fallzeile key={z.schluessel} z={z} mitBesitzer={mitBesitzer} />)}
          </div>
        ) : null}
      </div>
      {/* Der Fuß steht in allen drei Karten auf derselben Linie: Die Karten
          einer Reihe sind gleich hoch (Luca, 07.10.2026), der Leib nimmt
          den Rest. Ein leiser Satz, keine farbige Leiste. */}
      {spaeter.length ? (
        <button
          type="button" className="fall-spaeter" aria-expanded={spaeterOffen}
          onClick={() => setSpaeterOffen((o) => !o)}
        >
          {spaeterOffen ? t("fall.spaeterZu") : t("fall.spaeterAuf", { zahl: spaeter.length })}
        </button>
      ) : null}
      {status ? <p className="fall-status">{status}</p> : null}
    </div>
  );
}
